"use client";

import { c as _c } from "react/compiler-runtime";
import type { ApiWave } from "@/generated/models/ApiWave";
import { MyStreamWaveTab } from "@/types/waves.types";
import { ArrowUpTrayIcon, ArrowTopRightOnSquareIcon, ArrowTrendingUpIcon, ChartBarIcon, CheckIcon, ChevronDownIcon, FlagIcon, InformationCircleIcon, NoSymbolIcon, SparklesIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { AnimatePresence, motion } from "framer-motion";
import Link from "next/link";
import React, { useEffect, useId, useRef, useState } from "react";
import type { ComponentType, ReactNode, SVGProps } from "react";
import { useContentTab } from "../ContentTabContext";
import { useLayout } from "./layout/LayoutContext";
interface MyStreamWaveFAQProps {
  readonly wave: ApiWave;
}
type FaqSectionId = "intro" | "goals" | "not-goals" | "voting" | "submission" | "tdh" | "minting";
interface FaqSection {
  readonly id: FaqSectionId;
  readonly title: string;
  readonly Icon: ComponentType<SVGProps<SVGSVGElement>>;
  readonly Content: ComponentType;
}
interface QaItem {
  readonly question: string;
  readonly answer: ReactNode;
}
interface IconListItem {
  readonly key: string;
  readonly type: "check" | "cross";
  readonly content: ReactNode;
}
interface TimelinePhase {
  readonly label: string;
  readonly time: string;
  readonly timeClassName: string;
  readonly dotClassName: string;
  readonly description: string;
}
const DEFAULT_OPEN_SECTION_ID: FaqSectionId = "intro";
const LINKS = {
  artistBrief: "https://docs.google.com/presentation/d/1Aejko31qFkAIyu-Qc3Ao9tHQGbbaFCIcqrBj_kZzo_M/edit#slide=id.p1",
  nominationWave: "https://6529.io/waves/0ecb95d0-d8f2-48e8-8137-bfa71ee8593c",
  faqWave: "https://6529.io/waves/e2dae377-d27d-4a69-8b77-38d88fad4d01",
  tdhTheory: "https://x.com/punk6529/status/1906753171751412006",
  tdhFormula: "https://6529.io/network/tdh",
  subscriptions: "https://6529.io/about/subscriptions"
} as const;
const CONTAINER_CLASS_NAME = "tw-w-full tw-flex tw-flex-col tw-pt-4 lg:tw-pr-2 tw-overflow-y-auto no-scrollbar lg:tw-scrollbar-thin tw-scrollbar-thumb-iron-500 tw-scrollbar-track-iron-800 desktop-hover:hover:tw-scrollbar-thumb-iron-300 tw-h-full";
const FAQ_PANEL_ANIMATION_SECONDS = 0.32;
const FAQ_PANEL_TRANSITION = {
  height: {
    duration: FAQ_PANEL_ANIMATION_SECONDS,
    ease: [0.22, 1, 0.36, 1]
  },
  opacity: {
    duration: 0.18,
    ease: "easeOut"
  },
  y: {
    duration: 0.24,
    ease: [0.22, 1, 0.36, 1]
  }
} as const;
const FAQ_SCROLL_ALIGNMENT_DELAY_MS = FAQ_PANEL_ANIMATION_SECONDS * 1000 + 40;
const INTRO_QA: readonly QaItem[] = [{
  question: "What are we doing here?",
  answer: "Voting for the next The Memes card to be minted"
}, {
  question: "Who is voting?",
  answer: "Everyone with TDH"
}, {
  question: "What are we voting with?",
  answer: "TDH"
}, {
  question: "How do I learn more?",
  answer: "Keep reading"
}];
const EXCELLENCE_TYPES = ["Artistic excellence", "Memetic excellence", "Story-value excellence", "Several of the above"] as const;
const NOT_GOALS: readonly IconListItem[] = [{
  key: "vandalize",
  type: "cross",
  content: "We should not vandalize the collection for joke value."
}, {
  key: "sandbox",
  type: "cross",
  content: "The Memes are not a sandbox for goofing around among friends – they are, arguably, the most important large NFT art collection of 2022-2025."
}, {
  key: "thirty-years",
  type: "check",
  content: <>
        Our decisions should stand the test of time –{" "}
        <span className="tw-italic">
          &quot;can you imagine someone wanting to own the card 30 years from
          now?&quot;
        </span>{" "}
        is a pretty good test.
      </>
}, {
  key: "weird-excellent",
  type: "check",
  content: "Should we do weird things in The Memes? Sure, so long as they are also excellent."
}, {
  key: "weird-casual",
  type: "cross",
  content: "Should we do weird casual things that are not excellent? No."
}];
const VOTING_PHASES: readonly TimelinePhase[] = [{
  label: "Pre-voting",
  time: ">24h before",
  timeClassName: "tw-text-iron-500",
  dotClassName: "tw-border-iron-500",
  description: "You can add and remove TDH (Current Vote) to one or more submissions but it does not impact the final decision."
}, {
  label: "Voting",
  time: "0-24h before",
  timeClassName: "tw-text-primary-300",
  dotClassName: "tw-border-primary-400 tw-shadow-[0_0_10px_rgba(59,130,246,0.35)]",
  description: "You can add and remove TDH (Current Vote) to one more submissions, but it does impact the final decision as it starts counting towards the final 24HV. If you vote 100 TDH for the whole 24-hour period, it contributes 100 to the final vote. If you vote 100 TDH for 12 hours of the last 24 hours, it contributes 50 to the final vote."
}, {
  label: "Selection",
  time: "Checkpoint",
  timeClassName: "tw-text-emerald-400",
  dotClassName: "tw-border-emerald-400 tw-shadow-[0_0_10px_rgba(16,185,129,0.35)]",
  description: "The 24HV value at checkpoint time"
}];
const VOTING_QA: readonly QaItem[] = [{
  question: "Can you add and remove TDH at any time?",
  answer: "Yes"
}, {
  question: "Can you give TDH to more than one card?",
  answer: "Yes"
}, {
  question: "How much of your TDH can you use?",
  answer: "All of it - the aggregate of your positive and negative votes can add up to your total TDH"
}, {
  question: "Does your TDH get destroyed by voting?",
  answer: "No"
}, {
  question: "What happens when a card I voted for gets selected?",
  answer: "You get your TDH back"
}, {
  question: "What happens when I get TDH back from removing it from a card I voted for?",
  answer: "You get Current Vote TDH instantly but 24HV TDH decays and accrues over 24 hours"
}];
const ELIGIBILITY_RULES: readonly IconListItem[] = [{
  key: "meme-card-artist",
  type: "check",
  content: "You have previously dropped a Meme Card"
}, {
  key: "memes-nominee-rep",
  type: "check",
  content: 'You have been nominated in a decentralized way by one or more people giving you at least 50,000 "MemesNominee" rep'
}];
const SUBMISSION_QUALITY_NOTES = ["High quality submissions, in line with the collection mission, will do very well", "Low effort submissions will not get selected"] as const;
const MINT_STATS = [{
  label: "Mint price",
  value: "0.06529 ETH"
}, {
  label: "Edition sizes",
  value: "In the 300s right now"
}] as const;
function cx(...classes: ReadonlyArray<string | false | null | undefined>) {
  return classes.filter((className): className is string => Boolean(className)).join(" ");
}
function FaqLink(t0) {
  const $ = _c(10);
  const {
    href,
    children,
    className
  } = t0;
  const t1 = `${children} (opens in a new tab)`;
  let t2;
  if ($[0] !== className) {
    t2 = cx("tw-inline-flex tw-items-center tw-gap-1.5 tw-rounded-sm tw-text-sm tw-font-medium tw-text-primary-300 tw-no-underline tw-transition-colors tw-duration-200 focus-visible:tw-outline focus-visible:tw-outline-2 focus-visible:tw-outline-offset-2 focus-visible:tw-outline-primary-400 desktop-hover:hover:tw-text-primary-400", className);
    $[0] = className;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== children) {
    t3 = <span>{children}</span>;
    $[2] = children;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <ArrowTopRightOnSquareIcon className="tw-size-3.5 tw-flex-shrink-0 tw-opacity-70" aria-hidden="true" />;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] !== href || $[6] !== t1 || $[7] !== t2 || $[8] !== t3) {
    t5 = <Link href={href} target="_blank" rel="noopener noreferrer" aria-label={t1} className={t2}>{t3}{t4}</Link>;
    $[5] = href;
    $[6] = t1;
    $[7] = t2;
    $[8] = t3;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  return t5;
}
function QaPair(t0) {
  const $ = _c(7);
  const {
    question,
    children
  } = t0;
  let t1;
  if ($[0] !== question) {
    t1 = <h4 className="tw-mb-0 tw-text-sm tw-font-medium tw-leading-6 tw-text-iron-50">{question}</h4>;
    $[0] = question;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== children) {
    t2 = <div className="tw-text-sm tw-leading-6 tw-text-iron-400">{children}</div>;
    $[2] = children;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t1 || $[5] !== t2) {
    t3 = <div className="tw-space-y-1">{t1}{t2}</div>;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  return t3;
}
function QaList(t0) {
  const $ = _c(7);
  const {
    items,
    className
  } = t0;
  let t1;
  if ($[0] !== className) {
    t1 = cx("tw-flex tw-flex-col tw-gap-5", className);
    $[0] = className;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== items) {
    t2 = items.map(_temp);
    $[2] = items;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t1 || $[5] !== t2) {
    t3 = <div className={t1}>{t2}</div>;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  return t3;
}
function _temp(item) {
  return <QaPair key={item.question} question={item.question}>{item.answer}</QaPair>;
}
function NeatList(t0) {
  const $ = _c(4);
  const {
    items
  } = t0;
  let t1;
  if ($[0] !== items) {
    t1 = items.map(_temp2);
    $[0] = items;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t1) {
    t2 = <ul className="tw-my-4 tw-flex tw-list-none tw-flex-col tw-gap-3 tw-p-0">{t1}</ul>;
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  return t2;
}
function _temp2(item) {
  return <li key={item} className="tw-relative tw-pl-5 tw-text-sm tw-leading-6 tw-text-iron-300 before:tw-absolute before:tw-left-0 before:tw-top-[0.7rem] before:tw-size-1 before:tw-rounded-full before:tw-bg-iron-500 before:tw-content-['']">{item}</li>;
}
function AttentionBox(t0) {
  const $ = _c(2);
  const {
    children
  } = t0;
  let t1;
  if ($[0] !== children) {
    t1 = <div className="tw-my-6 tw-border-y-0 tw-border-l-2 tw-border-r-0 tw-border-solid tw-border-white/10 tw-py-1 tw-pl-4">{children}</div>;
    $[0] = children;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
}
function IconList(t0) {
  const $ = _c(4);
  const {
    items
  } = t0;
  let t1;
  if ($[0] !== items) {
    t1 = items.map(_temp3);
    $[0] = items;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t1) {
    t2 = <div className="tw-flex tw-flex-col tw-gap-4">{t1}</div>;
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  return t2;
}
function _temp3(item) {
  const Icon = item.type === "check" ? CheckIcon : XMarkIcon;
  const iconClassName = item.type === "check" ? "tw-text-emerald-400" : "tw-text-red tw-opacity-80";
  return <div key={item.key} className="tw-flex tw-items-start tw-gap-3 tw-text-sm tw-leading-6 tw-text-iron-300"><Icon className={cx("tw-mt-1 tw-size-4 tw-flex-shrink-0", iconClassName)} aria-hidden="true" /><span>{item.content}</span></div>;
}
function VotingTimeline() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div className="tw-relative tw-my-6 tw-flex tw-flex-col tw-gap-6 tw-pl-5"><div className="tw-absolute tw-bottom-1.5 tw-left-1 tw-top-1.5 tw-w-px tw-bg-gradient-to-b tw-from-iron-700 tw-via-primary-500/50 tw-to-emerald-500/70 tw-opacity-40" />{VOTING_PHASES.map(_temp4)}</div>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}
function _temp4(phase) {
  return <div key={phase.label} className="tw-relative"><div className={cx("tw-absolute -tw-left-5 tw-top-1.5 tw-z-10 tw-size-2.5 tw-rounded-full tw-border-2 tw-border-solid tw-bg-iron-950", phase.dotClassName)} /><div className="tw-mb-1 tw-flex tw-items-end tw-justify-between tw-gap-3"><h4 className="tw-mb-0 tw-text-sm tw-font-medium tw-text-iron-50">{phase.label}</h4><span className={cx("tw-flex-shrink-0 tw-text-[11px] tw-font-medium tw-uppercase tw-leading-4 tw-tracking-wider", phase.timeClassName)}>{phase.time}</span></div><p className="tw-mb-0 tw-text-[13px] tw-leading-6 tw-text-iron-300">{phase.description}</p></div>;
}
function StatsRow(t0) {
  const $ = _c(4);
  const {
    items
  } = t0;
  let t1;
  if ($[0] !== items) {
    t1 = items.map(_temp5);
    $[0] = items;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t1) {
    t2 = <div className="tw-my-5 tw-flex tw-flex-col tw-gap-5 tw-border-x-0 tw-border-y tw-border-solid tw-border-white/[0.04] tw-py-4 sm:tw-flex-row sm:tw-gap-8">{t1}</div>;
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  return t2;
}
function _temp5(item) {
  return <div key={item.label}><p className="tw-mb-1 tw-text-[11px] tw-font-medium tw-uppercase tw-leading-4 tw-tracking-wider tw-text-iron-500">{item.label}</p><p className="tw-mb-0 tw-text-sm tw-font-medium tw-leading-6 tw-text-iron-50">{item.value}</p></div>;
}
function IntroContent() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <QaList items={INTRO_QA} />;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}
function GoalsContent() {
  const $ = _c(5);
  let t0;
  let t1;
  let t2;
  let t3;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <p className="tw-mb-4">We are engaging in decentralized, networked curation of an important NFT art collection focused on decentralization.</p>;
    t1 = <p className="tw-mb-4">The goal is excellence, defined as a collection that will age well over decades.</p>;
    t2 = <p className="tw-mb-2 tw-text-iron-200">Excellence can come in many formats:</p>;
    t3 = <NeatList items={EXCELLENCE_TYPES} />;
    $[0] = t0;
    $[1] = t1;
    $[2] = t2;
    $[3] = t3;
  } else {
    t0 = $[0];
    t1 = $[1];
    t2 = $[2];
    t3 = $[3];
  }
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <>{t0}{t1}{t2}{t3}<AttentionBox><p className="tw-mb-3 tw-font-medium tw-text-iron-100">There is a long artist brief here that both artists and first-time voters should read.</p><p className="tw-mb-4 tw-text-[13px] tw-leading-6 tw-text-iron-400">It is a much more detailed FAQ than this one and if you want to be seriously engaged with this effort, you must read it! Otherwise you will bother everyone with basic questions that you could have answered by reading the presentation and we will quietly think a little less of you and nobody wants that.</p><FaqLink href={LINKS.artistBrief}>Read Artist Brief</FaqLink></AttentionBox><p className="tw-mb-0 tw-text-xs tw-leading-5 tw-text-iron-500">These FAQs are a summary of this presentation but not a substitute for reading it.</p></>;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  return t4;
}
function NotGoalsContent() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <IconList items={NOT_GOALS} />;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}
function VotingContent() {
  const $ = _c(4);
  let t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <p className="tw-mb-4">Voting is a continuous leaderboard – art is submitted and it stays on the leaderboard until it is removed by the artist who submitted it.</p>;
    t1 = <p className="tw-mb-4">Three times a week, the top submission on the leaderboard is selected to be the next card minted.</p>;
    $[0] = t0;
    $[1] = t1;
  } else {
    t0 = $[0];
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <p className="tw-mb-4">The checkpoint times are{" "}<span className="tw-font-medium tw-text-iron-100">Monday / Wednesday / Friday at 17:00 GMT</span>. The card will be minted at the next The Memes mint (e.g. Wednesday / Friday / Monday).</p>;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <>{t0}{t1}{t2}<p className="tw-mb-4">The metric that determines the winner is{" "}<span className="tw-font-medium tw-text-iron-100">24-Hour Vote (24HV)</span>.</p><p className="tw-mb-4">24HV is a weighted average of how much TDH was voted at any point in time ("Current Vote") during the last 24 hours before the checkpoint.</p><p className="tw-mb-7 tw-text-iron-400">The purpose of using 24HV is to avoid last second gaming of the vote. You can sort by 24HV and Current Vote. 24HV converges to Current Vote over a 24 hour period (noting that Current Vote is constantly changing).</p><h4 className="tw-mb-2 tw-text-sm tw-font-medium tw-text-iron-50">You can think of the voting in three phases:</h4><VotingTimeline /><QaList items={VOTING_QA} className="tw-mt-8 tw-border-x-0 tw-border-b-0 tw-border-t tw-border-solid tw-border-white/[0.05] tw-pt-6" /></>;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  return t3;
}
function SubmissionContent() {
  const $ = _c(6);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <p className="tw-mb-4">There are two ways you can be eligible to submit:</p>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <div className="tw-mb-8"><IconList items={ELIGIBILITY_RULES} /></div>;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <div className="tw-flex tw-flex-col tw-gap-6"><QaPair question="What if I do not know how to get nominated?"><p className="tw-mb-3">Go to the The Memes - Seeking Nomination wave and share your existing work and story and someone may nominate you:</p><FaqLink href={LINKS.nominationWave}>Nomination Wave</FaqLink></QaPair><p className="tw-mb-0">Once you are eligible, you can submit up to 3 cards at a time. If you remove a submission or one of your submissions is selected to be minted, you can submit another card to fill that spot.</p></div>;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <AttentionBox><p className="tw-mb-2 tw-font-medium tw-text-iron-100">It is very important to read the artist brief so that you don't waste your time:</p><NeatList items={SUBMISSION_QUALITY_NOTES} /></AttentionBox>;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <QaPair question="How do I get some artistic feedback as in the past?">DM @6529er, @teexels and @darrensrs into a group chat</QaPair>;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <>{t0}{t1}{t2}{t3}<div className="tw-flex tw-flex-col tw-gap-6">{t4}<QaPair question="What if I have general questions?"><p className="tw-mb-3">Go to the The Memes - FAQ wave (after you have read the artist brief)</p><FaqLink href={LINKS.faqWave}>FAQ Wave</FaqLink></QaPair></div></>;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  return t5;
}
function TdhContent() {
  const $ = _c(3);
  let t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <p className="tw-mb-2">TDH is the central metric of 6529 ecosystem.</p>;
    t1 = <p className="tw-mb-6">It is a quantitative metric reflecting how long you have held a 6529 NFT, how many you have held and how diverse your holdings are. It primarily rewards longevity.</p>;
    $[0] = t0;
    $[1] = t1;
  } else {
    t0 = $[0];
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <>{t0}{t1}<div className="tw-flex tw-flex-wrap tw-gap-x-6 tw-gap-y-3"><FaqLink href={LINKS.tdhTheory}>Theory</FaqLink><FaqLink href={LINKS.tdhFormula}>Formula</FaqLink></div></>;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  return t2;
}
function MintingContent() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <><p className="tw-mb-0">Everything about minting a Meme Card stays unchanged.</p><StatsRow items={MINT_STATS} /><p className="tw-mb-4">You can mint based on the Allowlist Phase you are in based on your TDH (P0, P1, P2) or public (if it goes to public).</p><p className="tw-mb-3">You can get priority within your Phase by subscribing in advance from your profile page. Details are here:</p><FaqLink href={LINKS.subscriptions} className="tw-mb-8">Subscriptions</FaqLink><p className="tw-mb-0 tw-font-medium tw-text-iron-100">Welcome aboard!</p></>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}
const FAQ_SECTIONS: readonly FaqSection[] = [{
  id: "intro",
  title: "Intro",
  Icon: InformationCircleIcon,
  Content: IntroContent
}, {
  id: "goals",
  title: "What are the goals of the voting?",
  Icon: FlagIcon,
  Content: GoalsContent
}, {
  id: "not-goals",
  title: "What Are Not The Goals Of The Voting",
  Icon: NoSymbolIcon,
  Content: NotGoalsContent
}, {
  id: "voting",
  title: "How Does Voting Work?",
  Icon: ChartBarIcon,
  Content: VotingContent
}, {
  id: "submission",
  title: "How Does Submission Work?",
  Icon: ArrowUpTrayIcon,
  Content: SubmissionContent
}, {
  id: "tdh",
  title: "What is TDH?",
  Icon: ArrowTrendingUpIcon,
  Content: TdhContent
}, {
  id: "minting",
  title: "Minting a Meme Card",
  Icon: SparklesIcon,
  Content: MintingContent
}];
function FaqAccordionItem(t0) {
  const $ = _c(37);
  const {
    section,
    sectionRef,
    isOpen,
    onToggle
  } = t0;
  const panelId = useId();
  const buttonId = `${panelId}-button`;
  const {
    Content,
    Icon
  } = section;
  const t1 = isOpen ? "tw-border-primary-500/50 tw-bg-[#171b24] tw-shadow-[0_12px_30px_-10px_rgba(0,0,0,0.8),inset_0_1px_0_rgba(255,255,255,0.05)]" : "tw-border-iron-700/70 tw-bg-iron-900/75 tw-shadow-[0_4px_20px_-5px_rgba(0,0,0,0.42)] desktop-hover:hover:-tw-translate-y-0.5 desktop-hover:hover:tw-border-iron-600/80 desktop-hover:hover:tw-bg-iron-800/70 desktop-hover:hover:tw-shadow-[0_8px_25px_-5px_rgba(0,0,0,0.6)]";
  let t2;
  if ($[0] !== t1) {
    t2 = cx("tw-group tw-overflow-hidden tw-rounded-xl tw-border tw-border-solid tw-backdrop-blur-xl tw-transition-all tw-duration-300 tw-ease-out", t1);
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const t3 = isOpen ? panelId : undefined;
  const t4 = isOpen ? "tw-text-primary-300" : "desktop-hover:group-hover:tw-text-iron-50";
  let t5;
  if ($[2] !== t4) {
    t5 = cx("tw-mr-3 tw-hidden tw-size-7 tw-flex-shrink-0 tw-items-center tw-justify-center tw-text-iron-400 tw-transition-colors tw-duration-200 sm:tw-flex", t4);
    $[2] = t4;
    $[3] = t5;
  } else {
    t5 = $[3];
  }
  let t6;
  if ($[4] !== Icon) {
    t6 = <Icon className="tw-size-4" aria-hidden="true" />;
    $[4] = Icon;
    $[5] = t6;
  } else {
    t6 = $[5];
  }
  let t7;
  if ($[6] !== t5 || $[7] !== t6) {
    t7 = <span className={t5}>{t6}</span>;
    $[6] = t5;
    $[7] = t6;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  let t8;
  if ($[9] !== section.title) {
    t8 = <span className="tw-min-w-0 tw-text-sm tw-font-medium tw-leading-6 tw-text-iron-50 md:tw-text-base">{section.title}</span>;
    $[9] = section.title;
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  let t9;
  if ($[11] !== t7 || $[12] !== t8) {
    t9 = <span className="tw-flex tw-min-w-0 tw-items-center">{t7}{t8}</span>;
    $[11] = t7;
    $[12] = t8;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  const t10 = isOpen && "tw-rotate-180 tw-text-iron-50";
  let t11;
  if ($[14] !== t10) {
    t11 = cx("tw-size-5 tw-flex-shrink-0 tw-text-iron-600 tw-transition-all tw-duration-300", t10);
    $[14] = t10;
    $[15] = t11;
  } else {
    t11 = $[15];
  }
  let t12;
  if ($[16] !== t11) {
    t12 = <ChevronDownIcon className={t11} aria-hidden="true" />;
    $[16] = t11;
    $[17] = t12;
  } else {
    t12 = $[17];
  }
  let t13;
  if ($[18] !== buttonId || $[19] !== isOpen || $[20] !== onToggle || $[21] !== t12 || $[22] !== t3 || $[23] !== t9) {
    t13 = <h3 className="tw-mb-0"><button id={buttonId} type="button" aria-expanded={isOpen} aria-controls={t3} onClick={onToggle} className="tw-flex tw-w-full tw-cursor-pointer tw-items-center tw-justify-between tw-gap-3 tw-border-0 tw-bg-transparent tw-px-4 tw-py-3 tw-text-left tw-transition-colors tw-duration-200 focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-inset focus-visible:tw-ring-primary-400/80 sm:tw-px-5 sm:tw-py-4 md:tw-py-3.5">{t9}{t12}</button></h3>;
    $[18] = buttonId;
    $[19] = isOpen;
    $[20] = onToggle;
    $[21] = t12;
    $[22] = t3;
    $[23] = t9;
    $[24] = t13;
  } else {
    t13 = $[24];
  }
  let t14;
  if ($[25] !== Content || $[26] !== buttonId || $[27] !== isOpen || $[28] !== panelId) {
    t14 = isOpen && <motion.div id={panelId} role="region" aria-labelledby={buttonId} initial={{
      height: 0,
      opacity: 0,
      y: -4
    }} animate={{
      height: "auto",
      opacity: 1,
      y: 0
    }} exit={{
      height: 0,
      opacity: 0,
      y: -2
    }} transition={FAQ_PANEL_TRANSITION} className="tw-overflow-hidden"><div className="tw-px-4 tw-pb-5 tw-text-sm tw-leading-6 tw-text-iron-300 sm:tw-px-5 sm:tw-pb-6 md:tw-pl-14"><Content /></div></motion.div>;
    $[25] = Content;
    $[26] = buttonId;
    $[27] = isOpen;
    $[28] = panelId;
    $[29] = t14;
  } else {
    t14 = $[29];
  }
  let t15;
  if ($[30] !== t14) {
    t15 = <AnimatePresence initial={false}>{t14}</AnimatePresence>;
    $[30] = t14;
    $[31] = t15;
  } else {
    t15 = $[31];
  }
  let t16;
  if ($[32] !== sectionRef || $[33] !== t13 || $[34] !== t15 || $[35] !== t2) {
    t16 = <section ref={sectionRef} className={t2}>{t13}{t15}</section>;
    $[32] = sectionRef;
    $[33] = t13;
    $[34] = t15;
    $[35] = t2;
    $[36] = t16;
  } else {
    t16 = $[36];
  }
  return t16;
}
const MyStreamWaveFAQ: React.FC<MyStreamWaveFAQProps> = t0 => {
  const $ = _c(18);
  const {
    setActiveContentTab
  } = useContentTab();
  const {
    faqViewStyle
  } = useLayout();
  const scrollContainerRef = useRef(null);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {};
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const sectionRefs = useRef(t1);
  const [openSectionId, setOpenSectionId] = useState(DEFAULT_OPEN_SECTION_ID);
  const [pendingScrollSectionId, setPendingScrollSectionId] = useState(null);
  let t2;
  let t3;
  if ($[1] !== setActiveContentTab) {
    t2 = () => {
      setActiveContentTab(MyStreamWaveTab.FAQ);
    };
    t3 = [setActiveContentTab];
    $[1] = setActiveContentTab;
    $[2] = t2;
    $[3] = t3;
  } else {
    t2 = $[2];
    t3 = $[3];
  }
  useEffect(t2, t3);
  let t4;
  let t5;
  if ($[4] !== openSectionId || $[5] !== pendingScrollSectionId) {
    t4 = () => {
      if (!pendingScrollSectionId || pendingScrollSectionId !== openSectionId) {
        return;
      }
      const timeoutId = globalThis.setTimeout(() => {
        const scrollContainer = scrollContainerRef.current;
        const targetSection = sectionRefs.current[pendingScrollSectionId];
        if (scrollContainer && targetSection) {
          const containerTop = scrollContainer.getBoundingClientRect().top;
          const sectionTop = targetSection.getBoundingClientRect().top;
          scrollContainer.scrollTo({
            top: scrollContainer.scrollTop + sectionTop - containerTop,
            behavior: "smooth"
          });
        }
        setPendingScrollSectionId(null);
      }, FAQ_SCROLL_ALIGNMENT_DELAY_MS);
      return () => globalThis.clearTimeout(timeoutId);
    };
    t5 = [openSectionId, pendingScrollSectionId];
    $[4] = openSectionId;
    $[5] = pendingScrollSectionId;
    $[6] = t4;
    $[7] = t5;
  } else {
    t4 = $[6];
    t5 = $[7];
  }
  useEffect(t4, t5);
  let t6;
  if ($[8] !== openSectionId) {
    t6 = sectionId => {
      const nextOpenSectionId = openSectionId === sectionId ? null : sectionId;
      setOpenSectionId(nextOpenSectionId);
      setPendingScrollSectionId(nextOpenSectionId);
    };
    $[8] = openSectionId;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  const handleSectionToggle = t6;
  let t7;
  if ($[10] !== handleSectionToggle || $[11] !== openSectionId) {
    t7 = FAQ_SECTIONS.map(section => <FaqAccordionItem key={section.id} section={section} sectionRef={element => {
      sectionRefs.current[section.id] = element;
    }} isOpen={openSectionId === section.id} onToggle={() => handleSectionToggle(section.id)} />);
    $[10] = handleSectionToggle;
    $[11] = openSectionId;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] !== t7) {
    t8 = <div className="tw-w-full tw-px-2 tw-pb-4 sm:tw-px-4"><div className="tw-flex tw-flex-col tw-gap-3">{t7}</div></div>;
    $[13] = t7;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  let t9;
  if ($[15] !== faqViewStyle || $[16] !== t8) {
    t9 = <div ref={scrollContainerRef} className={CONTAINER_CLASS_NAME} style={faqViewStyle}>{t8}</div>;
    $[15] = faqViewStyle;
    $[16] = t8;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  return t9;
};
export default MyStreamWaveFAQ;